'use client';

import * as React from 'react';
import { PanelLeft } from 'lucide-react';
import { SessionSidebar, type SidebarSessionGroup } from './session-sidebar';

export interface WebappShellProps {
  title: React.ReactNode;
  badge?: React.ReactNode;
  headerRight?: React.ReactNode;
  activeId?: string;
  onSelect?: (id: string) => void;
  onNewChat?: () => void;
  /** Real session rail (desktop app). Omit for the static mock rail (previews). */
  sessionGroups?: SidebarSessionGroup[];
  onDeleteSession?: (id: string) => void;
  sessionsLoading?: boolean;
  children: React.ReactNode;
}

/** ChatGPT-style S2 shell: collapsible session sidebar + top bar + conversation. */
export function WebappShell({
  title,
  badge,
  headerRight,
  activeId,
  onSelect,
  onNewChat,
  sessionGroups,
  onDeleteSession,
  sessionsLoading,
  children,
}: WebappShellProps) {
  const [collapsed, setCollapsed] = React.useState(false);
  return (
    <div className="flex h-full overflow-hidden bg-app text-fg">
      {collapsed ? null : (
        <SessionSidebar
          activeId={activeId}
          onSelect={onSelect}
          onNewChat={onNewChat}
          onCollapse={() => setCollapsed(true)}
          groups={sessionGroups}
          onDelete={onDeleteSession}
          loading={sessionsLoading}
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className="flex shrink-0 items-center justify-between gap-3 border-b border-border bg-surface px-4"
          style={{ height: 'var(--header-height)' }}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            {collapsed ? (
              <button
                type="button"
                onClick={() => setCollapsed(false)}
                aria-label="사이드바 열기"
                className="flex p-1 text-fg-muted hover:text-fg"
              >
                <PanelLeft size={17} />
              </button>
            ) : null}
            <span className="truncate text-md font-bold text-fg">{title}</span>
            {badge}
          </div>
          {headerRight ? <div className="flex items-center gap-2">{headerRight}</div> : null}
        </header>
        <div className="min-h-0 flex-1">{children}</div>
      </div>
    </div>
  );
}
